import {
  AddCircleOutline,
  DropdownMultiSelectInput,
  RichTextEditor,
} from '@aslanpay/portal-common-lib';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { isEmptyUrl, normalizeUrl } from 'app/utils/url';

import {
  BasicInformationFieldsProps,
  BENEFIT_NAME_MAX,
  BUTTON_LABEL_MAX,
  CategoryDropdownProps,
  DESCRIPTION_MAX,
  SHORT_DESCRIPTION_MAX,
} from './BasicInformationFields.types';

function CategoryDropdown({
  onOpenAddCategoryModal,
  optionsCategory,
  selectedCategoryIds = [],
}: CategoryDropdownProps) {
  const { setValue } = useFormContext();
  const [selected, setSelected] = useState<string[]>(selectedCategoryIds);

  const options = optionsCategory;

  const handleChange = (ids: string[]) => {
    setSelected(ids);
    setValue('categoryIds', ids, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    setSelected(selectedCategoryIds);
  }, [selectedCategoryIds]);

  if (options.length === 0) {
    return (
      <Flex
        align="center"
        gap="8px"
        cursor="pointer"
        w="fit-content"
        h="48px"
        onClick={onOpenAddCategoryModal}
      >
        <AddCircleOutline boxSize="20px" color="brand.purple" />
        <Text textStyle="smallMedium" color="brand.purple">
          Add category
        </Text>
      </Flex>
    );
  }

  return (
    <DropdownMultiSelectInput
      options={options}
      selected={selected}
      onChange={handleChange}
      placeholder="Choose category"
      footerAction={{
        icon: <AddCircleOutline color="brand.purple" boxSize="20px" />,
        label: 'Add category',
        onClick: onOpenAddCategoryModal,
      }}
    />
  );
}

function BasicInformationFields({
  onOpenAddCategoryModal,
  optionsCategory,
}: BasicInformationFieldsProps) {
  const {
    register,
    watch,
    trigger,
    control,
    formState: { errors, touchedFields },
  } = useFormContext();

  const benefitName = watch('benefitName') ?? '';
  const shortDescription = watch('shortDescription') ?? '';
  const employeeLink = watch('employeeLink') ?? '';
  const linkName = watch('linkName') ?? '';

  const categories = watch('categoryIds') ?? [];

  return (
    <Stack spacing="20px" mt="10px">
      {/* Benefit name + Category */}
      <Flex gap="20px" alignItems="flex-start">
        <FormControl
          isInvalid={!!errors?.benefitName && !!touchedFields?.benefitName}
          isRequired
          flex={1}
        >
          <FormLabel>Benefit name</FormLabel>
          <Box position="relative">
            <Input
              type="text"
              placeholder='E.g. "Dental Insurance"'
              maxLength={BENEFIT_NAME_MAX}
              {...register('benefitName', {
                onBlur: () => trigger('benefitName'),
              })}
            />
            <Text
              position="absolute"
              bottom="5px"
              right="5px"
              textStyle="extraExtraSmallMedium"
              color="brand.darkGray"
            >
              {benefitName.length} / {BENEFIT_NAME_MAX}
            </Text>
          </Box>
          <FormErrorMessage>{errors?.benefitName?.message as string}</FormErrorMessage>
        </FormControl>

        <FormControl flex={1}>
          <FormLabel>Category</FormLabel>
          <CategoryDropdown
            onOpenAddCategoryModal={onOpenAddCategoryModal}
            optionsCategory={optionsCategory}
            selectedCategoryIds={categories}
          />
        </FormControl>
      </Flex>

      {/* Short Description */}
      <FormControl isInvalid={!!errors?.shortDescription}>
        <FormLabel>Short description</FormLabel>
        <Box position="relative">
          <Input
            type="text"
            placeholder="A brief summary of the benefit"
            maxLength={SHORT_DESCRIPTION_MAX}
            {...register('shortDescription')}
          />
          <Text
            position="absolute"
            bottom="5px"
            right="5px"
            textStyle="extraExtraSmallMedium"
            color="brand.darkGray"
          >
            {shortDescription.length} / {SHORT_DESCRIPTION_MAX}
          </Text>
        </Box>
        <FormErrorMessage>{errors?.shortDescription?.message as string}</FormErrorMessage>
        <Text textStyle="extraExtraSmallMedium" color="brand.darkGray" mt="8px">
          Optional. This will be shown on the benefits list page to employees, if provided.
        </Text>
      </FormControl>

      {/* Description */}
      <FormControl isInvalid={!!errors?.description}>
        <FormLabel>Description</FormLabel>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <RichTextEditor
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="Provide a detailed description of the benefit, what it covers, and how employees can make the most of it."
              maxLength={DESCRIPTION_MAX}
            />
          )}
        />
        <FormErrorMessage>{errors?.description?.message as string}</FormErrorMessage>
      </FormControl>

      {/* Employee link */}
      <FormControl isInvalid={!!errors?.employeeLink && !!touchedFields?.employeeLink}>
        <FormLabel>Employee link</FormLabel>
        <Controller
          name="employeeLink"
          control={control}
          render={({ field }) => {
            const displayValue = field.value || 'https://';
            return (
              <Input
                value={displayValue}
                onChange={(e) => {
                  let val = e.target.value;
                  if (!val.startsWith('https://')) val = 'https://';
                  field.onChange(val);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData('text');
                  field.onChange(normalizeUrl(pasted) || 'https://');
                }}
                onBlur={() => {
                  const current = field.value || 'https://';
                  const normalized = current === 'https://' ? current : normalizeUrl(current);
                  if (normalized !== field.value) field.onChange(normalized);
                  field.onBlur();
                  trigger('employeeLink');
                }}
              />
            );
          }}
        />
        <FormErrorMessage>{errors?.employeeLink?.message as string}</FormErrorMessage>
        <Text textStyle="extraExtraSmallMedium" color="brand.darkGray" mt="8px">
          Optional link. It will be shown to employees as a button to access the benefit.
        </Text>
      </FormControl>

      {/* Button Label — only shown when employee link has a value */}
      {!isEmptyUrl(employeeLink) && (
        <FormControl isInvalid={!!errors?.linkName && !!touchedFields?.linkName} isRequired>
          <FormLabel>Button Label</FormLabel>
          <Box position="relative">
            <Input
              placeholder="e.g., Visit Provider"
              maxLength={BUTTON_LABEL_MAX}
              {...register('linkName', {
                required: !isEmptyUrl(employeeLink),
                onBlur: () => trigger('linkName'),
              })}
            />
            <Text
              position="absolute"
              bottom="5px"
              right="5px"
              textStyle="extraExtraSmallMedium"
              color="brand.darkGray"
            >
              {linkName.length}/{BUTTON_LABEL_MAX}
            </Text>
          </Box>
          <FormErrorMessage>{errors?.linkName?.message as string}</FormErrorMessage>
        </FormControl>
      )}
    </Stack>
  );
}

export default BasicInformationFields;
